//upscaler.ts — GPU upscale of the Lucy feed, rendered once at the exact display grid.
//Pass 1: Catmull-Rom resample with FSR-style anti-ringing clamp (output limited to the
//nearest 2×2 input texels, the mechanism FSR's EASU documents for ring-free scaling).
//Pass 2: FSR RCAS sharpen — solves for maximum sharpness before clipping and carries a
//noise gate, which matters on generative video that shimmers — plus subtle film grain
//to mask generative smoothness. Deterministic on purpose: this layer invents nothing.
//The canvas backing store matches CSS pixels × devicePixelRatio exactly, so the browser
//never resamples our output a second time (that softening was a measured defect).

const VERT = `#version 300 es
void main() {
	vec2 pos = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
	gl_Position = vec4(pos * 2.0 - 1.0, 0.0, 1.0);
}`

const FRAG_RESAMPLE = `#version 300 es
precision highp float;
uniform sampler2D src;
uniform vec2 srcSize;
//Destination rectangle of the video inside the canvas, in physical pixels: xy offset, zw size.
uniform vec4 rect;
out vec4 color;

vec4 sampleCR(vec2 uv) {
	vec2 samplePos = uv * srcSize;
	vec2 texPos1 = floor(samplePos - 0.5) + 0.5;
	vec2 f = samplePos - texPos1;
	vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
	vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
	vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
	vec2 w3 = f * f * (-0.5 + 0.5 * f);
	vec2 w12 = w1 + w2;
	vec2 offset12 = w2 / w12;
	vec2 texPos0 = (texPos1 - 1.0) / srcSize;
	vec2 texPos3 = (texPos1 + 2.0) / srcSize;
	vec2 texPos12 = (texPos1 + offset12) / srcSize;
	vec4 result =
		texture(src, vec2(texPos0.x, texPos0.y)) * w0.x * w0.y +
		texture(src, vec2(texPos12.x, texPos0.y)) * w12.x * w0.y +
		texture(src, vec2(texPos3.x, texPos0.y)) * w3.x * w0.y +
		texture(src, vec2(texPos0.x, texPos12.y)) * w0.x * w12.y +
		texture(src, vec2(texPos12.x, texPos12.y)) * w12.x * w12.y +
		texture(src, vec2(texPos3.x, texPos12.y)) * w3.x * w12.y +
		texture(src, vec2(texPos0.x, texPos3.y)) * w0.x * w3.y +
		texture(src, vec2(texPos12.x, texPos3.y)) * w12.x * w3.y +
		texture(src, vec2(texPos3.x, texPos3.y)) * w3.x * w3.y;
	return result;
}

void main() {
	vec2 rel = (gl_FragCoord.xy - rect.xy) / rect.zw;
	if (any(lessThan(rel, vec2(0.0))) || any(greaterThan(rel, vec2(1.0)))) {
		color = vec4(0.0, 0.0, 0.0, 1.0);
		return;
	}
	vec3 result = sampleCR(rel).rgb;
	//Anti-ring: negative kernel lobes can overshoot at hard edges; clamping to the
	//nearest 2x2 neighbourhood removes all ringing (per FSR's ffx_fsr1.h).
	vec2 texel = 1.0 / srcSize;
	vec2 base = (floor(rel * srcSize - 0.5) + 0.5) * texel;
	vec3 c00 = texture(src, base).rgb;
	vec3 c10 = texture(src, base + vec2(texel.x, 0.0)).rgb;
	vec3 c01 = texture(src, base + vec2(0.0, texel.y)).rgb;
	vec3 c11 = texture(src, base + texel).rgb;
	vec3 lo = min(min(c00, c10), min(c01, c11));
	vec3 hi = max(max(c00, c10), max(c01, c11));
	color = vec4(clamp(result, lo, hi), 1.0);
}`

//FSR RCAS, plus luma-windowed grain inside the video rectangle only.
const FRAG_SHARPEN = `#version 300 es
precision highp float;
uniform sampler2D src;
uniform vec2 outSize;
uniform vec4 rect;
uniform float sharpConst;
uniform float grainSeed;
out vec4 color;

float luma(vec3 c) { return c.g + 0.5 * (c.r + c.b); }

void main() {
	vec2 xy = gl_FragCoord.xy;
	vec2 px = 1.0 / outSize;
	vec2 uv = xy * px;
	vec3 e = texture(src, uv).rgb;
	vec3 b = texture(src, uv + vec2(0.0, px.y)).rgb;
	vec3 d = texture(src, uv - vec2(px.x, 0.0)).rgb;
	vec3 f = texture(src, uv + vec2(px.x, 0.0)).rgb;
	vec3 h = texture(src, uv - vec2(0.0, px.y)).rgb;

	float bL = luma(b);
	float dL = luma(d);
	float eL = luma(e);
	float fL = luma(f);
	float hL = luma(h);

	//Noise gate: high-frequency luma variance reads as noise/shimmer, so back the
	//sharpening off there instead of amplifying it.
	float range = max(max(max(bL, dL), max(fL, hL)), eL) - min(min(min(bL, dL), min(fL, hL)), eL);
	float nz = clamp(abs(0.25 * (bL + dL + fL + hL) - eL) / max(range, 1e-4), 0.0, 1.0);
	nz = 1.0 - 0.5 * nz;

	//Solve for the maximum negative lobe that cannot clip, per RCAS.
	vec3 mn4 = min(min(b, d), min(f, h));
	vec3 mx4 = max(max(b, d), max(f, h));
	vec3 hitMin = mn4 / (4.0 * mx4 + 1e-4);
	vec3 hitMax = (1.0 - mx4) / (4.0 * mn4 - 4.0 - 1e-4);
	vec3 lobeRGB = max(-hitMin, hitMax);
	float lobe = max(-0.1875, min(max(max(lobeRGB.r, lobeRGB.g), lobeRGB.b), 0.0)) * sharpConst * nz;
	vec3 result = (lobe * (b + d + f + h) + e) / (4.0 * lobe + 1.0);

	//Grain: ±1.5/255 at midtones, fading to zero in shadows/highlights; masks the
	//plastic smoothness of generative video without reading as noise.
	vec2 rel = (xy - rect.xy) / rect.zw;
	if (all(greaterThanEqual(rel, vec2(0.0))) && all(lessThanEqual(rel, vec2(1.0)))) {
		float l = clamp(eL * 0.5, 0.0, 1.0);
		float g = fract(sin(dot(xy + grainSeed, vec2(12.9898, 78.233))) * 43758.5453) - 0.5;
		result += g * 0.012 * (4.0 * l * (1.0 - l));
	}
	color = vec4(clamp(result, 0.0, 1.0), 1.0);
}`

//RCAS sharpness in FSR stops: 0 = maximum. sharpConst = exp2(-stops).
const RCAS_STOPS = 0.3
const MAX_SIDE = 4096

export class Upscaler {
	private readonly gl: WebGL2RenderingContext
	private readonly resample: WebGLProgram
	private readonly sharpen: WebGLProgram
	private readonly sourceTex: WebGLTexture
	private readonly midTex: WebGLTexture
	private readonly framebuffer: WebGLFramebuffer
	private width = 0
	private height = 0
	private frame = 0

	constructor(private readonly canvas: HTMLCanvasElement) {
		const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: false })
		if (!gl) throw new Error('WebGL2 unavailable')
		this.gl = gl
		this.resample = buildProgram(gl, FRAG_RESAMPLE)
		this.sharpen = buildProgram(gl, FRAG_SHARPEN)
		this.sourceTex = makeTexture(gl)
		this.midTex = makeTexture(gl)
		const framebuffer = gl.createFramebuffer()
		if (!framebuffer) throw new Error('Could not create framebuffer')
		this.framebuffer = framebuffer
	}

	render(video: HTMLVideoElement): void {
		if (!video.videoWidth || !video.videoHeight) return
		if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return
		this.renderSource(video, video.videoWidth, video.videoHeight)
	}

	//Same pipeline for any uploadable source — the sidecar feeds decoded ImageData through
	//here so Apple's SR output still gets the display-grid resample + RCAS + grain finish.
	renderSource(source: TexImageSource, sourceWidth: number, sourceHeight: number): void {
		const gl = this.gl
		if (!sourceWidth || !sourceHeight) return

		//The display grid: CSS size × devicePixelRatio, so one resample lands on real pixels.
		const dpr = Math.min(
			window.devicePixelRatio || 1,
			MAX_SIDE / Math.max(this.canvas.clientWidth, this.canvas.clientHeight, 1),
		)
		const outWidth = Math.max(1, Math.round(this.canvas.clientWidth * dpr))
		const outHeight = Math.max(1, Math.round(this.canvas.clientHeight * dpr))
		if (outWidth !== this.width || outHeight !== this.height) {
			this.width = outWidth
			this.height = outHeight
			this.canvas.width = outWidth
			this.canvas.height = outHeight
			gl.bindTexture(gl.TEXTURE_2D, this.midTex)
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, outWidth, outHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
		}

		//Cover when the frame shape matches the screen, contain (letterbox) when it does not
		//— same policy the plain-video path applies via object-fit.
		const shapesMatch = sourceHeight > sourceWidth === outHeight > outWidth
		const scale = shapesMatch
			? Math.max(outWidth / sourceWidth, outHeight / sourceHeight)
			: Math.min(outWidth / sourceWidth, outHeight / sourceHeight)
		const rectW = sourceWidth * scale
		const rectH = sourceHeight * scale
		const rectX = (outWidth - rectW) / 2
		const rectY = (outHeight - rectH) / 2

		gl.bindTexture(gl.TEXTURE_2D, this.sourceTex)
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)

		gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer)
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.midTex, 0)
		gl.viewport(0, 0, outWidth, outHeight)
		gl.useProgram(this.resample)
		gl.activeTexture(gl.TEXTURE0)
		gl.bindTexture(gl.TEXTURE_2D, this.sourceTex)
		gl.uniform1i(gl.getUniformLocation(this.resample, 'src'), 0)
		gl.uniform2f(gl.getUniformLocation(this.resample, 'srcSize'), sourceWidth, sourceHeight)
		gl.uniform4f(gl.getUniformLocation(this.resample, 'rect'), rectX, rectY, rectW, rectH)
		gl.drawArrays(gl.TRIANGLES, 0, 3)

		this.frame = (this.frame + 1) % 1024
		gl.bindFramebuffer(gl.FRAMEBUFFER, null)
		gl.viewport(0, 0, outWidth, outHeight)
		gl.useProgram(this.sharpen)
		gl.bindTexture(gl.TEXTURE_2D, this.midTex)
		gl.uniform1i(gl.getUniformLocation(this.sharpen, 'src'), 0)
		gl.uniform2f(gl.getUniformLocation(this.sharpen, 'outSize'), outWidth, outHeight)
		gl.uniform4f(gl.getUniformLocation(this.sharpen, 'rect'), rectX, rectY, rectW, rectH)
		gl.uniform1f(gl.getUniformLocation(this.sharpen, 'sharpConst'), Math.pow(2, -RCAS_STOPS))
		gl.uniform1f(gl.getUniformLocation(this.sharpen, 'grainSeed'), this.frame * 7.13)
		gl.drawArrays(gl.TRIANGLES, 0, 3)
	}

	dispose(): void {
		const gl = this.gl
		gl.deleteProgram(this.resample)
		gl.deleteProgram(this.sharpen)
		gl.deleteTexture(this.sourceTex)
		gl.deleteTexture(this.midTex)
		gl.deleteFramebuffer(this.framebuffer)
	}
}

function buildProgram(gl: WebGL2RenderingContext, fragSource: string): WebGLProgram {
	const program = gl.createProgram()
	if (!program) throw new Error('Could not create program')
	for (const [type, source] of [
		[gl.VERTEX_SHADER, VERT],
		[gl.FRAGMENT_SHADER, fragSource],
	] as const) {
		const shader = gl.createShader(type)
		if (!shader) throw new Error('Could not create shader')
		gl.shaderSource(shader, source)
		gl.compileShader(shader)
		if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
			throw new Error(`Shader compile failed: ${gl.getShaderInfoLog(shader) ?? 'unknown'}`)
		}
		gl.attachShader(program, shader)
	}
	gl.linkProgram(program)
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
		throw new Error(`Program link failed: ${gl.getProgramInfoLog(program) ?? 'unknown'}`)
	}
	return program
}

function makeTexture(gl: WebGL2RenderingContext): WebGLTexture {
	const texture = gl.createTexture()
	if (!texture) throw new Error('Could not create texture')
	gl.bindTexture(gl.TEXTURE_2D, texture)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
	return texture
}
